/**
 * KO · CALIBRADOR DE TALLA
 * ---------------------------------------------------------------------------
 * Le pregunta al cliente lo que sabe de sí mismo: "¿Qué talla sueles usar?" y
 * (si la familia tiene más de una opción confirmada) "¿Cómo te gusta llevarla?",
 * y le responde directo: "Coge la M", con una línea que explica por qué.
 *
 * La respuesta sale de la REGLA DE TALLAJE DEL FABRICANTE de cada familia de
 * prenda (ko-fit-finder-data.json > families[productType].rule). Es un criterio
 * confirmado por Adrián, no una medida: así se presenta al cliente, que siempre
 * es remitido a la guía de tallas de la ficha (app Pify) para las medidas
 * exactas. Nada de este archivo inventa tallas ni cifras.
 *
 * Siempre da respuesta: no depende de que la ficha tenga referencia de modelo.
 * Si la tiene, se muestra como contexto bajo la recomendación.
 *
 * Vía secundaria ("Mide una camiseta tuya"): solo en familias cuyo punto de
 * medida está documentado (campo "measureHint" del JSON). Compara el ancho
 * medido con la tabla real en cm (prenda contra prenda); la tabla NO se pinta.
 *
 * El número de WhatsApp nunca está aquí: llega por data-whatsapp-number desde
 * el ajuste del bloque. Vacío o inválido = sin botón.
 *
 * Analítica: window.dataLayer (el tema ya carga Google Tag Manager), sin
 * dependencias nuevas y SIN datos personales (nunca se envían medidas).
 * ---------------------------------------------------------------------------
 */
(function () {
  if (customElements.get('ko-fit-finder')) return;

  // Tallas que se ofrecen siempre como "talla habitual", más las que tenga la prenda.
  var BASE_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
  var FALLBACK_SCALE = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL'];

  // Vía "prenda contra prenda": límites para no dar por buena una medida absurda.
  //  - GARMENT_CONTOUR_WARN: un ancho de un lado (axila a axila) por encima de 80 cm
  //    no existe en una camiseta de adulto; casi seguro han medido el contorno completo.
  //  - GARMENT_INPUT_MARGIN: fuera de [primera talla - 8, última talla + 8] se rechaza.
  //  - GARMENT_OFF_TABLE: a más de 4 cm por debajo de la talla más pequeña o por encima
  //    de la más grande NO se recomienda ("queda fuera de nuestra tabla").
  var GARMENT_CONTOUR_WARN = 80; // cm
  var GARMENT_INPUT_MARGIN = 8; // cm
  var GARMENT_OFF_TABLE = 4; // cm

  var GUIDE_LINE = 'Las medidas exactas de cada talla están en la guía de tallas de arriba.';

  var DEFAULT_WA_MESSAGE =
    'Hola, quiero elegir talla para {producto}. Normalmente uso la {talla}.';

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function metres(cm) {
    return (cm / 100).toFixed(2).replace('.', ',');
  }

  // 2 -> "2", 2.5 -> "2,5"
  function num(n) {
    return String(Math.round(n * 10) / 10).replace('.', ',');
  }

  // Normaliza el número de WhatsApp del ajuste del bloque:
  //  - solo dígitos; se quita el "00" internacional inicial;
  //  - 9 dígitos que empiezan por 6, 7 o 9 = número español sin prefijo -> se antepone 34;
  //  - cualquier otra cosa demasiado corta o larga = inválido (sin botón, nunca enlace roto).
  function normalizeWhatsapp(raw) {
    var d = String(raw || '').replace(/\D/g, '');
    if (d.indexOf('00') === 0) d = d.slice(2);
    if (d.length === 9 && /^[679]/.test(d)) d = '34' + d;
    return d.length >= 10 && d.length <= 15 ? d : '';
  }

  class KoFitFinder extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.handle = this.dataset.productHandle || '';
      this.type = this.dataset.productType || '';
      // OJO: no llamarla "title": HTMLElement.title pintaría un tooltip.
      this.productTitle = this.dataset.productTitle || '';
      this.src = this.dataset.src;

      this.productSizes = (this.dataset.sizes || '')
        .split('|')
        .map(function (s) {
          return s.trim();
        })
        .filter(Boolean);

      this.refs = [];
      this.family = null;
      this.rule = null;
      this.scale = FALLBACK_SCALE.slice();
      this.hasGarment = false;
      this.habitual = '';
      this.option = null;
      this.note = '';
      this.state = 'loading';
      this.loaded = false;
      this.failed = false;

      this.sizesEl = this.querySelector('.ko-fit-finder__sizes');
      this.fitStep = this.querySelector('.ko-fit-finder__step--fit');
      this.fitBox = this.querySelector('.ko-fit-finder__toggle');
      this.resultEl = this.querySelector('.ko-fit-finder__result');
      this.refine = this.querySelector('.ko-fit-finder__refine');
      this.refineToggle = this.querySelector('.ko-fit-finder__refine-toggle');
      this.measureForm = this.querySelector('.ko-fit-finder__measure');
      this.garmentResult = this.querySelector('.ko-fit-finder__garment-result');
      this.waWrap = this.querySelector('.ko-fit-finder__wa-wrap');

      if (!this.sizesEl || !this.resultEl || !this.src) return;

      // Tallas habituales: XS..XXL siempre, y las extra (3XL, 4XL) si la prenda las tiene.
      var self = this;
      this.scale = FALLBACK_SCALE.filter(function (s) {
        return BASE_SIZES.indexOf(s) !== -1 || self.productSizes.indexOf(s) !== -1;
      });
      this.sizesEl.innerHTML = this.scale
        .map(function (s) {
          return (
            '<button type="button" class="ko-fit-finder__size-btn" data-size="' + escapeHtml(s) +
            '" aria-pressed="false">' + escapeHtml(s) + '</button>'
          );
        })
        .join('');

      // Un solo listener delegado: tallas, opciones de ajuste, WhatsApp y "afinar".
      this.addEventListener('click', function (e) {
        var t = e.target && e.target.closest ? e.target : null;
        if (!t) return;

        var sizeBtn = t.closest('.ko-fit-finder__size-btn');
        if (sizeBtn) return self.chooseSize(sizeBtn.getAttribute('data-size'));

        var fitBtn = t.closest('.ko-fit-finder__fit-btn');
        if (fitBtn) return self.chooseFit(fitBtn.getAttribute('data-fit'));

        if (t.closest('.ko-fit-finder__wa')) {
          return self.track('whatsapp_click', { state: self.state });
        }

        if (t.closest('.ko-fit-finder__refine-toggle')) return self.toggleRefine();
      });

      if (this.measureForm) {
        this.measureForm.addEventListener('submit', function (e) {
          e.preventDefault();
          self.calculateGarment();
        });
      }

      // Apertura del desplegable (una vez): denominador de todos los demás eventos.
      var details = this.closest ? this.closest('details') : null;
      if (details) {
        details.addEventListener('toggle', function () {
          if (details.open && !self._opened) {
            self._opened = true;
            self.track('open');
          }
        });
      }

      this.loadData();
    }

    // ---- Analítica (window.dataLayer / GTM; sin datos personales) -----------

    track(action, extra) {
      try {
        window.dataLayer = window.dataLayer || [];
        var payload = {
          event: 'ko_fit_finder',
          fit_finder_action: action,
          product_handle: this.handle,
          product_type: this.type
        };
        if (extra) {
          Object.keys(extra).forEach(function (k) {
            payload['fit_finder_' + k] = extra[k];
          });
        }
        window.dataLayer.push(payload);
      } catch (e) {
        /* la analítica nunca debe romper el calibrador */
      }
    }

    // ---- WhatsApp (solo si la tienda ha configurado el número) -------------

    hasWhatsapp() {
      return normalizeWhatsapp(this.dataset.whatsappNumber) !== '';
    }

    whatsappHref() {
      var digits = normalizeWhatsapp(this.dataset.whatsappNumber);
      if (!digits) return '';

      var msg = this.dataset.whatsappMessage || DEFAULT_WA_MESSAGE;
      msg = msg.replace(/\{producto\}/g, this.productTitle);
      msg = msg.replace(/\{talla\}/g, this.habitual || '___');

      return 'https://wa.me/' + digits + '?text=' + encodeURIComponent(msg);
    }

    renderWa() {
      if (!this.waWrap) return;
      var href = this.whatsappHref();
      this.waWrap.innerHTML = href
        ? '<a class="ko-fit-finder__wa" href="' + escapeHtml(href) +
          '" target="_blank" rel="noopener">Escribir por WhatsApp</a>'
        : '';
    }

    // ---- Carga -------------------------------------------------------------------

    show(el, on) {
      if (el) el.style.display = on ? '' : 'none';
    }

    loadData() {
      var self = this;

      fetch(this.src)
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then(function (data) {
          self.family = data.families ? data.families[self.type] || null : null;
          // Referencias: primero las de ESTA prenda, luego las válidas para toda la familia.
          var own = (data.references && data.references[self.handle]) || [];
          var famRefs = (self.family && self.family.references) || [];
          self.refs = own.concat(famRefs);

          // Ajustes por prenda concreta: nota informativa y/o regla propia (sustituye a la de la familia).
          var ov = (data.overrides && data.overrides[self.handle]) || {};
          self.note = ov.note || '';
          self.rule = ov.rule || (self.family && self.family.rule) || null;
          self.loaded = true;

          if (!self.rule || !self.rule.options || !self.rule.options.length) {
            // Familia sin regla confirmada: no se muestra nada.
            self.style.display = 'none';
            return;
          }

          // Escala de tallas del JSON (si existe) para respetar el orden oficial.
          if (data.sizeScale && data.sizeScale.length) {
            var extra = self.productSizes;
            self.scale = data.sizeScale.filter(function (s) {
              return BASE_SIZES.indexOf(s) !== -1 || extra.indexOf(s) !== -1;
            });
          }

          self.hasGarment = !!(
            self.family.measureHint &&
            self.family.measureDim &&
            self.family.measurements &&
            self.family.garmentName
          );

          self.setupFitOptions();
          self.setupGarmentUi();
          self.state = 'ready';

          if (self.habitual) self.compute(false);
        })
        .catch(function (err) {
          console.error('KO Calibrador: no se pudo cargar ko-fit-finder-data.json', err);
          self.failed = true;
          self.state = 'error';
          self.showError();
        });
    }

    // La remisión a la guía de tallas (app Pify) va SIEMPRE, en todas las respuestas.
    guideHtml() {
      return '<p class="ko-fit-finder__guide">' + GUIDE_LINE + '</p>';
    }

    showError() {
      this.resultEl.hidden = false;
      this.resultEl.innerHTML =
        '<p class="ko-fit-finder__notice">No hemos podido cargar el calibrador ahora mismo. ' +
        GUIDE_LINE + '</p>';
      this.renderWa();
    }

    // ---- Opciones de "cómo te gusta llevarla" (de la regla de la familia) -------

    setupFitOptions() {
      var opts = this.rule.options;
      this.option = null;
      for (var i = 0; i < opts.length; i++) {
        if (opts[i].default) this.option = opts[i];
      }
      if (!this.option) this.option = opts[0];

      // Con una sola opción confirmada no se pregunta nada.
      if (opts.length > 1 && this.fitBox) {
        var selected = this.option.id;
        this.fitBox.innerHTML = opts
          .map(function (o) {
            var on = o.id === selected;
            return (
              '<button type="button" class="ko-fit-finder__toggle-btn ko-fit-finder__fit-btn' +
              (on ? ' is-active' : '') + '" data-fit="' + escapeHtml(o.id) + '" aria-pressed="' +
              (on ? 'true' : 'false') + '">' + escapeHtml(o.label) + '</button>'
            );
          })
          .join('');
        this.show(this.fitStep, true);
      } else {
        this.show(this.fitStep, false);
      }
    }

    chooseFit(id) {
      if (!this.rule) return;
      var found = null;
      this.rule.options.forEach(function (o) {
        if (o.id === id) found = o;
      });
      if (!found) return;
      this.option = found;

      Array.prototype.slice.call(this.querySelectorAll('.ko-fit-finder__fit-btn')).forEach(function (b) {
        var on = b.getAttribute('data-fit') === id;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      if (this.habitual) this.compute(true);
    }

    chooseSize(size) {
      this.habitual = size;
      Array.prototype.slice.call(this.querySelectorAll('.ko-fit-finder__size-btn')).forEach(function (b) {
        var on = b.getAttribute('data-size') === size;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      if (this.failed) return this.showError();
      if (!this.loaded) return; // se calcula en cuanto lleguen los datos
      this.compute(true);
    }

    // ---- La recomendación ----------------------------------------------------------

    nearestAvailable(wantIdx) {
      var scale = this.scale;
      var avail = this.productSizes
        .map(function (s) {
          return scale.indexOf(s);
        })
        .filter(function (i) {
          return i !== -1;
        });
      if (!avail.length) return wantIdx;

      var best = avail[0];
      avail.forEach(function (i) {
        var d = Math.abs(i - wantIdx);
        var bd = Math.abs(best - wantIdx);
        // En un empate se queda la más grande.
        if (d < bd || (d === bd && i > best)) best = i;
      });
      return best;
    }

    referencesHtml() {
      if (!this.refs.length) return '';
      var items = this.refs.map(function (r) {
        var phrase = r.phrase || '{who} mide {m} m, pesa {kg} kg y lleva la {size}';
        return phrase
          .replace('{who}', r.who || 'el modelo de la foto')
          .replace('{m}', metres(r.heightCm))
          .replace('{kg}', escapeHtml(r.weightKg))
          .replace('{size}', escapeHtml(r.size));
      });
      return '<p class="ko-fit-finder__ref">Como referencia: ' + items.join('; ') + '.</p>';
    }

    compute(userAction) {
      if (!this.rule || !this.habitual || !this.option) return;

      var scale = this.scale;
      var idx = scale.indexOf(this.habitual);
      if (idx === -1) return;

      var want = idx + this.option.offset;
      var edge = '';
      if (want < 0) {
        want = 0;
        edge = 'min';
      } else if (want > scale.length - 1) {
        want = scale.length - 1;
        edge = 'max';
      }

      var desired = scale[want];
      var chosenIdx = want;
      var missing = '';

      // La talla calculada puede no existir en esta prenda: se da la más cercana, y se dice.
      if (this.productSizes.length && this.productSizes.indexOf(desired) === -1) {
        chosenIdx = this.nearestAvailable(want);
        missing = 'La ' + desired + ' no la tenemos en esta prenda: la más cercana es la ' + scale[chosenIdx] + '.';
      }
      var chosen = scale[chosenIdx];

      var why = String(this.option.why || '').replace(/\{habitual\}/g, this.habitual);

      var html =
        '<p class="ko-fit-finder__answer">Coge la <span class="ko-fit-finder__size">' +
        escapeHtml(chosen) + '</span></p>' +
        '<p class="ko-fit-finder__why">' + escapeHtml(why) + '</p>';

      if (edge === 'min') {
        html += '<p class="ko-fit-finder__fit-note">Ya es la talla más pequeña que hacemos.</p>';
      } else if (edge === 'max') {
        html += '<p class="ko-fit-finder__fit-note">Ya es la talla más grande que hacemos.</p>';
      }
      if (missing) html += '<p class="ko-fit-finder__fit-note">' + escapeHtml(missing) + '</p>';

      if (this.note) html += '<p class="ko-fit-finder__alert">' + escapeHtml(this.note) + '</p>';

      html += this.referencesHtml();
      html +=
        '<p class="ko-fit-finder__disclaimer">Es una orientación según cómo tira esta prenda, no una medida exacta.' +
        (this.hasWhatsapp() ? ' ¿Dudas? Escríbenos por WhatsApp y lo miramos contigo.' : '') + '</p>' +
        this.guideHtml();

      this.state = 'recommended';
      this.resultEl.hidden = false;
      this.resultEl.innerHTML = html;
      this.renderWa();
      this.track('recommendation', {
        habitual: this.habitual,
        fit: this.option.id,
        size: chosen
      });
    }

    // ---- Vía secundaria: "mide una camiseta tuya" ----------------------------------

    setupGarmentUi() {
      this.show(this.refine, this.hasGarment);
      if (!this.hasGarment) return;
      var name = this.family.garmentName;

      if (this.refineToggle) {
        this.refineToggle.textContent = '¿Quieres afinar más? Mide una ' + name + ' tuya';
      }

      var hint = this.querySelector('.ko-fit-finder__measure-hint');
      if (hint) {
        var how = this.family.measureInstructions || ('Mídela ' + this.family.measureHint + '.');
        hint.textContent =
          'Coge una ' + name + ' que ya te quede como te gusta. ' + how + ' ' +
          'Compárala con una ' + name + ' de corte parecido a esta: si la tuya es holgada y esta es ajustada, la comparación no vale.';
      }

      var label = this.querySelector('.ko-fit-finder__measure label');
      if (label) label.textContent = 'Ancho de tu ' + name + ' (cm)';
    }

    toggleRefine() {
      if (!this.measureForm) return;
      var open = this.measureForm.style.display === 'none';
      this.show(this.measureForm, open);
      if (this.refineToggle) this.refineToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) this.track('refine_open');
    }

    garmentNotice(text) {
      this.garmentResult.hidden = false;
      this.garmentResult.innerHTML = '<p class="ko-fit-finder__notice">' + text + '</p>';
      this.bringIntoView(this.garmentResult);
    }

    bringIntoView(el) {
      try {
        el.focus({ preventScroll: true });
        var reduce =
          window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest' });
      } catch (e) {
        /* el aria-live ya avisa */
      }
    }

    calculateGarment() {
      if (!this.loaded || !this.family || !this.hasGarment) {
        this.garmentNotice(
          this.failed ? 'No hemos podido cargar el calibrador ahora mismo. ' + GUIDE_LINE : 'Un segundo, estamos cargando…'
        );
        return;
      }

      var x = parseFloat(String(this.measureForm.garment.value).replace(',', '.'));

      if (!x || x < 20) {
        this.garmentNotice('Revisa la medida: ponla en centímetros, por ejemplo 52.');
        return;
      }

      // Un ancho de un lado por encima de 80 cm es casi seguro el contorno completo.
      if (x > GARMENT_CONTOUR_WARN) {
        this.garmentNotice('Parece que has medido todo el contorno. Mide solo de axila a axila, en línea recta.');
        return;
      }

      var fam = this.family;
      var dim = fam.measureDim;
      var tol = typeof fam.tolerance === 'number' ? fam.tolerance : 0;
      var name = fam.garmentName;
      var rows = fam.sizeOrder.map(function (s) {
        return { size: s, v: fam.measurements[s][dim] };
      });

      // Fuera de [primera talla - 8, última talla + 8]: error de medición, no se calcula nada.
      if (x < rows[0].v - GARMENT_INPUT_MARGIN || x > rows[rows.length - 1].v + GARMENT_INPUT_MARGIN) {
        this.garmentNotice(
          'Esa medida no cuadra con una ' + name + '. Revisa que sea el ancho de un lado, en línea recta y en centímetros, por ejemplo 52.'
        );
        return;
      }

      var html = '';
      var kind;

      // 1) Coincide (dentro de la tolerancia documentada)
      var hit = rows.filter(function (r) {
        return Math.abs(r.v - x) <= tol;
      })[0];

      if (hit) {
        kind = 'match';
        html =
          '<p class="ko-fit-finder__why">Tu ' + name + ' mide ' + num(x) + ' cm de ancho y coincide con nuestra talla ' +
          escapeHtml(hit.size) + ' (' + num(hit.v) + ' cm' + (tol ? ', con ±' + num(tol) + ' cm de tolerancia' : '') + ').</p>' +
          '<p class="ko-fit-finder__answer">La que más se parece: <span class="ko-fit-finder__size">' +
          escapeHtml(hit.size) + '</span></p>';
      } else if (x < rows[0].v) {
        if (rows[0].v - x > GARMENT_OFF_TABLE) {
          kind = 'out_of_table';
          html = this.outOfTableHtml(x, rows);
        } else {
          kind = 'below';
          html =
            '<p class="ko-fit-finder__why">Tu ' + name + ' mide ' + num(x) + ' cm y nuestra talla más pequeña (' +
            escapeHtml(rows[0].size) + ') mide ' + num(rows[0].v) + ' cm: la ' + escapeHtml(rows[0].size) +
            ' te quedará ' + num(rows[0].v - x) + ' cm más holgada que tu ' + name + '.</p>';
        }
      } else if (x > rows[rows.length - 1].v) {
        var last = rows[rows.length - 1];
        if (x - last.v > GARMENT_OFF_TABLE) {
          kind = 'out_of_table';
          html = this.outOfTableHtml(x, rows);
        } else {
          kind = 'above';
          html =
            '<p class="ko-fit-finder__why">Tu ' + name + ' mide ' + num(x) + ' cm y nuestra talla más grande (' +
            escapeHtml(last.size) + ') mide ' + num(last.v) + ' cm: la ' + escapeHtml(last.size) +
            ' te quedará ' + num(x - last.v) + ' cm más justa que tu ' + name + '.</p>';
        }
      } else {
        // 2) Entre dos tallas: se dice tal cual, con la diferencia real, sin decidir por él.
        var lo = null;
        var hi = null;
        for (var i = 0; i < rows.length - 1; i++) {
          if (rows[i].v < x && x < rows[i + 1].v) {
            lo = rows[i];
            hi = rows[i + 1];
            break;
          }
        }
        kind = 'between';
        html =
          '<p class="ko-fit-finder__why">Tu ' + name + ' mide ' + num(x) + ' cm y queda entre la ' +
          escapeHtml(lo.size) + ' (' + num(lo.v) + ' cm) y la ' + escapeHtml(hi.size) + ' (' + num(hi.v) + ' cm).</p>' +
          '<p class="ko-fit-finder__why">La ' + escapeHtml(lo.size) + ' te quedará ' + num(x - lo.v) +
          ' cm más justa que tu ' + name + ' y la ' + escapeHtml(hi.size) + ', ' + num(hi.v - x) +
          ' cm más holgada. Tú decides según cómo te guste llevarla.</p>';
      }

      html +=
        '<p class="ko-fit-finder__disclaimer">Vale si tu ' + name + ' es de corte parecido al de esta: ' +
        'el corte cambia cómo cae, y no comparamos entre cortes distintos.' +
        (this.hasWhatsapp() ? ' ¿Dudas? Escríbenos por WhatsApp y lo miramos contigo.' : '') + '</p>' +
        this.guideHtml();

      this.garmentResult.hidden = false;
      this.garmentResult.innerHTML = html;
      this.renderWa();
      this.track('garment_result', { result: kind });
      this.bringIntoView(this.garmentResult);
    }

    // Medida válida pero fuera de la tabla por más de GARMENT_OFF_TABLE cm: no se recomienda.
    outOfTableHtml(x, rows) {
      return (
        '<p class="ko-fit-finder__why">Tu ' + this.family.garmentName + ' mide ' + num(x) +
        ' cm y queda fuera de nuestra tabla (va de ' + num(rows[0].v) + ' a ' + num(rows[rows.length - 1].v) +
        ' cm). No queremos que elijas talla a ciegas: ' +
        (this.hasWhatsapp()
          ? 'escríbenos por WhatsApp y te ayudamos.'
          : 'mira la guía de tallas de arriba y revisa la medida.') +
        '</p>'
      );
    }
  }

  customElements.define('ko-fit-finder', KoFitFinder);
})();
